/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Disaster recovery (/dr): protected VMs and their replication health, protection groups (with what
 * their sites can do together), protection policies and sites, one tab each (/dr/<tab>). A row
 * opens a drawer (/dr/<tab>/<id>). The API refuses what the person's role does not allow and what
 * Swift cannot honour; its message is shown where the action was taken.
 */

import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router";
import { ListChecks, LoaderCircle, MapPin, Plus, SearchX, ShieldCheck, ShieldPlus, Timer, Users } from "lucide-react";

import {
  AddWorkloadsDialog,
  ConfirmDialog,
  GroupDialog,
  PolicyDialog,
  ProtectDialog,
  SiteDialog,
} from "@src/components/modules/DrModule/DrDialogs";
import { CHECK_TONES, PLAN_STATES, PlanDialog, PlanDrawer } from "@src/components/modules/DrModule/DrPlans";
import DrAlertsView from "@src/components/modules/DrModule/DrAlerts";
import DrClientSettingsView from "@src/components/modules/DrModule/DrClientSettings";
import { ClientAccessBanner, DrOverviewView } from "@src/components/modules/DrModule/DrClients";
import userStore from "@src/stores/UserStore";
import { GroupDrawer, WorkloadDrawer } from "@src/components/modules/DrModule/DrDrawers";
import {
  drErrorMessage,
  formatSeconds,
  needsAttention,
  platformName,
  replicationStateInfo,
  workloadDisplayState,
} from "@src/components/modules/DrModule/drUtils";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import PageHeader from "@src/components/smart/PageHeader";
import { DetailsTabs } from "@src/components/ui/DetailsKit";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import ListToolbar, {
  FilterSelect,
  RefreshButton,
  Segmented,
  ToolbarCount,
  ToolbarSearch,
  ToolbarSpacer,
  matchesSearch,
  useListQuery,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import {
  Cell,
  ListHeader,
  ListRow,
  Muted,
  Pill,
  Primary,
  Secondary,
  Stack,
  T9,
  formatShortDateTime,
  pluralise,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import { Notice } from "@src/components/ui/OpsDialog";
import { apiErrorStatus } from "@src/sources/CutoverSource";
import drSource from "@src/sources/DrSource";
import endpointSource from "@src/sources/EndpointSource";
import transferSource from "@src/sources/TransferSource";
import notificationStore from "@src/stores/NotificationStore";

import type { DrGroup, DrPlan, DrPolicy, DrSite, DrWorkload, DrWorkloadAction } from "@src/@types/Dr";
import type { Endpoint } from "@src/@types/Endpoint";
import type { TransferItem } from "@src/@types/MainItem";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

export const TABS = ["vms", "groups", "plans", "policies", "sites", "alerts", "settings", "clients"] as const;
export type DrTab = (typeof TABS)[number];
const POLL_MS = 20000;

export const VM_COLUMNS: ListColumn[] = [
  { key: "vm", label: "VM", width: "minmax(220px, 2fr)" },
  { key: "group", label: "Protection group", width: "minmax(150px, 1.2fr)" },
  { key: "status", label: "Status", width: "150px" },
  { key: "replication", label: "Replication", width: "150px" },
  { key: "point", label: "Last recovery point", width: "150px" },
  { key: "risk", label: "Data at risk / RPO", width: "150px" },
];
const GROUP_COLUMNS: ListColumn[] = [
  { key: "group", label: "Protection group", width: "minmax(200px, 2fr)" },
  { key: "sites", label: "From → to", width: "minmax(200px, 1.6fr)" },
  { key: "policy", label: "Policy", width: "minmax(140px, 1fr)" },
  { key: "vms", label: "VMs", width: "110px" },
  { key: "check", label: "Capabilities", width: "170px" },
];
const PLAN_COLUMNS: ListColumn[] = [
  { key: "name", label: "Plan", width: "minmax(200px, 2fr)" },
  { key: "groups", label: "Protection groups", width: "minmax(180px, 1.5fr)" },
  { key: "version", label: "Version", width: "90px" },
  { key: "state", label: "State", width: "140px" },
  { key: "validation", label: "Last validation", width: "minmax(170px, 1fr)" },
];
const POLICY_COLUMNS: ListColumn[] = [
  { key: "name", label: "Policy", width: "minmax(200px, 2fr)" },
  { key: "rpo", label: "RPO", width: "100px" },
  { key: "interval", label: "Syncs every", width: "110px" },
  { key: "consistency", label: "Consistency", width: "170px" },
  { key: "points", label: "Restore points", width: "120px" },
  { key: "groups", label: "Groups", width: "90px" },
];
const SITE_COLUMNS: ListColumn[] = [
  { key: "name", label: "Site", width: "minmax(200px, 1.5fr)" },
  { key: "kind", label: "Kind", width: "120px" },
  { key: "endpoints", label: "Endpoints", width: "minmax(220px, 2fr)" },
  { key: "groups", label: "Used by", width: "130px" },
  { key: "created", label: "Created", width: "104px" },
];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;
const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  overflow: hidden;
`;
const TabsBar = styled.div`
  padding: 4px 12px 0 12px;
  border-bottom: 1px solid ${T9.slate200};
`;
const Scroll = styled.div`
  flex-grow: 1;
  min-height: 0;
  overflow: auto;
`;
const Inner = styled.div<{ minWidth: number }>`
  min-width: ${props => props.minWidth}px;
`;
const Footnote = styled.div`
  padding: 10px 16px;
  border-top: 1px solid ${T9.slate200};
  font-size: 12px;
  color: ${T9.slate500};
`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px;
  color: ${T9.slate500};
  font-size: 14px;
  .spin {
    animation: dr-page-spin 1s linear infinite;
  }
  @keyframes dr-page-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const NewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: ${T9.brand600};
  color: white;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${T9.brand700};
  }
`;
const Steps = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin: 0;
  padding: 12px 16px;
  list-style: none;
  border-bottom: 1px solid ${T9.slate200};
  background: ${T9.slate50};
  font-size: 13px;
  color: ${T9.slate600};
  li {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .n {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 9999px;
    background: ${T9.slate200};
    color: ${T9.slate700};
    font-size: 11px;
    font-weight: 600;
  }
  .done .n {
    background: var(--t9-success-50);
    color: var(--t9-success-700);
  }
`;

type Dialog =
  | { kind: "site"; site?: DrSite | null }
  | { kind: "policy"; policy?: DrPolicy | null }
  | { kind: "group"; group?: DrGroup | null }
  | { kind: "add"; groupId?: string | null }
  | { kind: "protect"; workload: DrWorkload }
  | { kind: "plan"; plan?: DrPlan | null }
  | {
      kind: "confirm";
      title: string;
      message: React.ReactNode;
      confirmLabel: string;
      busyLabel: string;
      danger?: boolean;
      reason?: boolean;
      run: (reason: string) => Promise<void>;
    };

type Props = {
  tab: DrTab;
  selectedId: string | null;
  query: ListQuery;
  onNavigate: (path: string) => void;
};
type State = {
  sites: DrSite[];
  policies: DrPolicy[];
  groups: DrGroup[];
  workloads: DrWorkload[];
  plans: DrPlan[];
  endpoints: Endpoint[];
  transfers: TransferItem[];
  loading: boolean;
  loaded: boolean;
  disabled: boolean;
  error: string | null;
  dialog: Dialog | null;
  /** Warnings an action returned, shown in the VM's drawer until it closes. */
  warnings: { [workloadId: string]: string[] };
  /** Active DR alerts (the Alerts tab's count); null until known. */
  alertCount: number | null;
};

@observer
class DrPageContent extends React.Component<Props, State> {
  state: State = {
    sites: [],
    policies: [],
    groups: [],
    workloads: [],
    plans: [],
    endpoints: [],
    transfers: [],
    loading: false,
    loaded: false,
    disabled: false,
    error: null,
    dialog: null,
    warnings: {},
    alertCount: null,
  };

  setAlertCount = (alertCount: number) => this.setState({ alertCount });

  offSwiftRefresh?: () => void;

  timer: any = null;

  unmounted = false;

  componentDidMount() {
    document.title = "Disaster recovery · SwiftIQ";
    this.offSwiftRefresh = onSwiftRefresh(() => this.load());
    this.load();
    this.timer = setInterval(() => this.load(true), POLL_MS);
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.offSwiftRefresh?.();
    clearInterval(this.timer);
  }

  async load(quiet = false) {
    if (!quiet) this.setState({ loading: true });
    try {
      const [sites, policies, groups, workloads, endpoints, transfers, plans] = await Promise.all([
        drSource.sites({ skipLog: quiet }),
        drSource.policies({ skipLog: true }),
        drSource.groups({ skipLog: true }),
        drSource.workloads({ skipLog: true }),
        endpointSource.getEndpoints(true).catch(() => []),
        transferSource.getTransfers({ skipLog: true, quietError: true }).catch(() => []),
        drSource.plans({ skipLog: true }).catch(() => [] as DrPlan[]),
      ]);
      const alerts = await drSource.alerts().catch(() => null);
      if (this.unmounted) return;
      this.setState({ sites, policies, groups, workloads, endpoints, transfers, plans, loaded: true, error: null, disabled: false,
        alertCount: alerts ? alerts.length : null });
    } catch (err: any) {
      if (this.unmounted) return;
      if (apiErrorStatus(err) === 404) {
        this.setState({ disabled: true, loaded: true });
      } else if (!quiet) {
        this.setState({ error: drErrorMessage(err, "Could not load disaster recovery.") });
      }
    } finally {
      if (!this.unmounted && !quiet) this.setState({ loading: false });
    }
  }

  // ---- lookups ---------------------------------------------------------------------------------

  group = (id?: string | null) => this.state.groups.find(g => g.id === id);

  policy = (id?: string | null) => this.state.policies.find(p => p.id === id);

  site = (id?: string | null) => this.state.sites.find(s => s.id === id);

  endpointRef = (id?: string | null) => {
    for (const s of this.state.sites) {
      const found = s.endpoints.find(e => e.id === id);
      if (found) return found;
    }
    const ep = this.state.endpoints.find(e => e.id === id);
    return ep ? { id: ep.id, name: ep.name, type: ep.type } : undefined;
  };

  targetEndpoint = (group?: DrGroup) => this.site(group?.target_site_id)?.endpoints[0];

  transferName = (id?: string | null) => {
    const t = this.state.transfers.find(x => x.id === id);
    return t ? t.notes || t.instances?.[0] || t.id : null;
  };

  // ---- actions ---------------------------------------------------------------------------------

  closeDialog = () => this.setState({ dialog: null });

  goTab(tab: DrTab, id?: string | null) {
    this.props.onNavigate(`/dr/${tab}${id ? `/${id}` : ""}${window.location.search}`);
  }

  async afterChange(message: string) {
    notificationStore.alert(message, "success");
    this.setState({ dialog: null });
    await this.load(true);
  }

  confirm(dialog: Omit<Extract<Dialog, { kind: "confirm" }>, "kind">) {
    this.setState({ dialog: { kind: "confirm", ...dialog } });
  }

  workloadAction(w: DrWorkload, action: DrWorkloadAction | "delete") {
    const run = async (a: DrWorkloadAction, options: any, done: string) => {
      const updated = await drSource.workloadAction(w.id, a, options);
      if (updated.warnings?.length) this.setState(prev => ({ warnings: { ...prev.warnings, [w.id]: updated.warnings! } }));
      await this.afterChange(done);
    };
    if (action === "protect") {
      this.setState({ dialog: { kind: "protect", workload: w } });
    } else if (action === "sync") {
      run("sync", {}, `A sync of ${w.name} started.`).catch(err =>
        notificationStore.alert(drErrorMessage(err, "Could not start a sync."), "error"),
      );
    } else if (action === "resume") {
      run("resume", {}, `The replication of ${w.name} resumed.`).catch(err =>
        notificationStore.alert(drErrorMessage(err, "Could not resume the replication."), "error"),
      );
    } else if (action === "pause") {
      this.confirm({
        title: `Pause the replication of ${w.name}?`,
        message: "No new sync starts until it is resumed; one already running finishes. The recovery point keeps ageing meanwhile, and the RPO can be breached.",
        confirmLabel: "Pause",
        busyLabel: "Pausing…",
        reason: true,
        run: reason => run("pause", reason ? { reason } : {}, `The replication of ${w.name} is paused.`),
      });
    } else if (action === "unprotect") {
      this.confirm({
        title: `Stop protecting ${w.name}?`,
        message: (
          <>
            SwiftIQ stops syncing it and drops its protection. The transfer{" "}
            <strong>{this.transferName(w.replication?.transfer_id) || w.replication?.transfer_id}</strong> and its replica
            data are kept: delete them from Transfers if they are no longer needed. The history stays.
          </>
        ),
        confirmLabel: "Unprotect",
        busyLabel: "Unprotecting…",
        danger: true,
        reason: true,
        run: reason => run("unprotect", reason ? { reason } : {}, `${w.name} is no longer protected.`),
      });
    } else if (action === "delete") {
      this.confirm({
        title: `Remove ${w.name} from disaster recovery?`,
        message: "It is not protected; this only takes it out of its protection group.",
        confirmLabel: "Remove",
        busyLabel: "Removing…",
        danger: true,
        run: async () => {
          await drSource.remove("workload", w.id);
          this.goTab("vms");
          await this.afterChange(`${w.name} was removed.`);
        },
      });
    }
  }

  deleteRecord(kind: "site" | "policy" | "protection_group", id: string, name: string, tab: DrTab) {
    const what = { site: "site", policy: "policy", protection_group: "protection group" }[kind];
    this.confirm({
      title: `Delete the ${what} ${name}?`,
      message: "SwiftIQ records it in the audit log first. A record still in use cannot be deleted.",
      confirmLabel: "Delete",
      busyLabel: "Deleting…",
      danger: true,
      run: async () => {
        await drSource.remove(kind, id);
        this.goTab(tab);
        await this.afterChange(`The ${what} ${name} was deleted.`);
      },
    });
  }

  // ---- toolbar and lists -----------------------------------------------------------------------

  renderSetup() {
    const s = this.state;
    const steps = [
      { label: "Sites", done: s.sites.length > 0 },
      { label: "Protection policy", done: s.policies.length > 0 },
      { label: "Protection group", done: s.groups.length > 0 },
      { label: "Add VMs", done: s.workloads.length > 0 },
      { label: "Protect them", done: s.workloads.some(w => w.replication) },
    ];
    if (steps.every(x => x.done)) return null;
    return (
      <Steps aria-label="Setting up disaster recovery">
        {steps.map((step, i) => (
          <li key={step.label} className={step.done ? "done" : undefined}>
            <span className="n">{step.done ? "✓" : i + 1}</span>
            {step.label}
          </li>
        ))}
      </Steps>
    );
  }

  renderToolbar() {
    const { tab, query: q } = this.props;
    const s = this.state;
    const newButton = (label: string, onClick: () => void, testId: string) => (
      <NewButton type="button" onClick={onClick} data-test-id={testId}>
        <Plus size={16} /> {label}
      </NewButton>
    );
    let count = "";
    let action: React.ReactNode = null;
    let filters: React.ReactNode = null;
    if (tab === "vms") {
      const all = this.vmsFiltered("state");
      filters = (
        <>
          <Segmented
            label="Show"
            value={q.get("state")}
            onChange={v => q.set({ state: v })}
            options={[
              { value: "", label: "All" },
              { value: "attention", label: "Needs attention", count: all.filter(needsAttention).length },
              { value: "protected", label: "Protected", count: all.filter(w => w.replication).length },
              { value: "unprotected", label: "Not protected", count: all.filter(w => !w.replication).length },
            ]}
          />
          <FilterSelect
            label="Group"
            value={q.get("group")}
            onChange={v => q.set({ group: v })}
            options={s.groups.map(g => ({ value: g.id, label: g.name }))}
            allLabel="Any"
          />
        </>
      );
      count = pluralise(this.vmsFiltered().length, "VM");
      action = s.groups.length ? newButton("Add VMs", () => this.setState({ dialog: { kind: "add" } }), "dr-new-vms") : null;
    } else if (tab === "groups") {
      count = pluralise(s.groups.length, "group");
      action = newButton("New group", () => this.setState({ dialog: { kind: "group" } }), "dr-new-group");
    } else if (tab === "plans") {
      count = pluralise(s.plans.filter(pl => pl.state !== "ARCHIVED").length, "plan");
      action = s.groups.length ? newButton("New plan", () => this.setState({ dialog: { kind: "plan" } }), "dr-new-plan") : null;
    } else if (tab === "policies") {
      count = pluralise(s.policies.length, "policy", "policies");
      action = newButton("New policy", () => this.setState({ dialog: { kind: "policy" } }), "dr-new-policy");
    } else {
      count = pluralise(s.sites.length, "site");
      action = newButton("New site", () => this.setState({ dialog: { kind: "site" } }), "dr-new-site");
    }
    return (
      <ListToolbar>
        <ToolbarSearch value={q.get("q")} placeholder="Search" onChange={v => q.set({ q: v })} width={220} />
        {filters}
        <ToolbarSpacer />
        <ToolbarCount>{count}</ToolbarCount>
        <RefreshButton onClick={() => this.load()} />
        {action}
      </ListToolbar>
    );
  }

  vmsFiltered(except?: string): DrWorkload[] {
    const q = this.props.query;
    const text = q.get("q");
    const state = q.get("state");
    const group = q.get("group");
    return this.state.workloads.filter(w => {
      if (except !== "state" && state === "attention" && !needsAttention(w)) return false;
      if (except !== "state" && state === "protected" && !w.replication) return false;
      if (except !== "state" && state === "unprotected" && w.replication) return false;
      if (group && w.group_id !== group) return false;
      if (text) {
        return matchesSearch(text, [w.name, w.instance, w.os_type, this.group(w.group_id)?.name, w.state, w.replication?.state]);
      }
      return true;
    });
  }

  emptyOr(rows: React.ReactNode[], empty: React.ReactNode) {
    if (this.state.loading && !this.state.loaded) {
      return (
        <Loading>
          <LoaderCircle className="spin" size={20} />
          Loading disaster recovery…
        </Loading>
      );
    }
    return rows.length ? rows : empty;
  }

  vmActions(w: DrWorkload): DropdownAction[] {
    const rep = w.replication;
    const paused = rep?.state === "PAUSED";
    return [
      { label: "Open", action: () => this.goTab("vms", w.id) },
      { label: "Protect…", hidden: Boolean(rep), action: () => this.workloadAction(w, "protect") },
      { label: "Sync now", hidden: !rep || paused, action: () => this.workloadAction(w, "sync") },
      { label: "Pause…", hidden: !rep || paused, action: () => this.workloadAction(w, "pause") },
      { label: "Resume", hidden: !paused, action: () => this.workloadAction(w, "resume") },
      { label: "Open transfer", hidden: !rep, action: () => this.props.onNavigate(`/transfers/${rep?.transfer_id}`) },
      { label: "Unprotect…", hidden: !rep, action: () => this.workloadAction(w, "unprotect") },
      { label: "Remove…", hidden: Boolean(rep), action: () => this.workloadAction(w, "delete") },
    ];
  }

  renderVms() {
    const records = this.vmsFiltered();
    const rows = records.map(w => {
      const state = workloadDisplayState(w);
      const rep = w.replication;
      const repState = replicationStateInfo(rep?.state);
      const ep = this.endpointRef(w.source_endpoint_id);
      return (
        <ListRow
          key={w.id}
          columns={VM_COLUMNS}
          onClick={() => this.goTab("vms", w.id)}
          selected={this.props.selectedId === w.id}
          actions={this.vmActions(w)}
          testId="dr-vm-row"
        >
          <Stack>
            <Primary title={w.instance}>{w.name}</Primary>
            <Secondary>
              {ep?.name || "?"} · {platformName(ep?.type)}
              {w.os_type ? ` · ${w.os_type}` : ""}
            </Secondary>
          </Stack>
          <Cell>{this.group(w.group_id)?.name || <Muted>—</Muted>}</Cell>
          <div>
            <Pill tone={state.tone} icon={state.icon} spin={state.spin} title={w.state_reason || undefined}>
              {state.label}
            </Pill>
          </div>
          <div>
            {rep ? (
              <Pill tone={repState.tone} icon={repState.icon} spin={repState.spin} title={rep.degraded_reason || rep.state_reason || undefined}>
                {repState.label}
              </Pill>
            ) : (
              <Muted>—</Muted>
            )}
          </div>
          <Cell title={rep?.last_sync_at ? formatShortDateTime(rep.last_sync_at) : undefined}>
            {rep?.last_sync_at ? timeAgo(rep.last_sync_at) : <Muted>{rep ? "none yet" : "—"}</Muted>}
          </Cell>
          <div>
            {rep && rep.lag_seconds !== null ? (
              <Pill tone={rep.rpo_met ? "success" : "danger"}>
                {formatSeconds(rep.lag_seconds)} / {formatSeconds(rep.rpo_seconds)}
              </Pill>
            ) : rep ? (
              <Muted>RPO {formatSeconds(rep.rpo_seconds)}</Muted>
            ) : (
              <Muted>—</Muted>
            )}
          </div>
        </ListRow>
      );
    });
    const filtered = Boolean(this.props.query.get("q") || this.props.query.get("state") || this.props.query.get("group"));
    return (
      <>
        <ListHeader columns={VM_COLUMNS} />
        {this.emptyOr(
          rows,
          filtered ? (
            <EmptyState compact icon={SearchX} tone="neutral" title="No VMs match these filters." />
          ) : (
            <EmptyState
              compact
              icon={ShieldPlus}
              title="No VMs in disaster recovery yet"
              message={
                this.state.groups.length
                  ? "Add VMs to a protection group, then protect each one with its replica transfer."
                  : "Set up a site for each side, a protection policy and a protection group first."
              }
              secondaryLabel={this.state.groups.length ? "Add VMs" : "Open sites"}
              onSecondary={() => (this.state.groups.length ? this.setState({ dialog: { kind: "add" } }) : this.goTab("sites"))}
            />
          ),
        )}
      </>
    );
  }

  renderGroups() {
    const text = this.props.query.get("q");
    const records = this.state.groups.filter(
      g => !text || matchesSearch(text, [g.name, g.description, this.site(g.source_site_id)?.name, this.site(g.target_site_id)?.name]),
    );
    const rows = records.map(g => {
      const vms = this.state.workloads.filter(w => w.group_id === g.id);
      const level = g.capabilities[0];
      return (
        <ListRow
          key={g.id}
          columns={GROUP_COLUMNS}
          onClick={() => this.goTab("groups", g.id)}
          selected={this.props.selectedId === g.id}
          actions={[
            { label: "Open", action: () => this.goTab("groups", g.id) },
            { label: "Add VMs…", action: () => this.setState({ dialog: { kind: "add", groupId: g.id } }) },
            { label: "Edit…", action: () => this.setState({ dialog: { kind: "group", group: g } }) },
            { label: "Delete…", hidden: vms.length > 0, action: () => this.deleteRecord("protection_group", g.id, g.name, "groups") },
          ]}
          testId="dr-group-row"
        >
          <Stack>
            <Primary>{g.name}</Primary>
            {g.description ? <Secondary>{g.description}</Secondary> : null}
          </Stack>
          <Stack>
            <Primary style={{ fontSize: 13, fontWeight: 500 }}>
              {this.site(g.source_site_id)?.name || "?"} → {this.site(g.target_site_id)?.name || "?"}
            </Primary>
            <Secondary>
              {g.capabilities.map(c => `${platformName(c.source_platform)} → ${platformName(c.target_platform)}`).join(", ")}
            </Secondary>
          </Stack>
          <Stack>
            <Primary style={{ fontSize: 13, fontWeight: 500 }}>{this.policy(g.policy_id)?.name || "?"}</Primary>
            <Secondary>RPO {formatSeconds(this.policy(g.policy_id)?.rpo_seconds)}</Secondary>
          </Stack>
          <Cell>
            {vms.filter(w => w.replication).length} of {vms.length} protected
          </Cell>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {level ? <Pill tone="brand">Level {level.protection_level}</Pill> : null}
            {g.warnings.length ? (
              <Pill tone="warning" title={g.warnings.join("\n")}>
                {pluralise(g.warnings.length, "warning")}
              </Pill>
            ) : (
              <Pill tone="success">All tested</Pill>
            )}
          </div>
        </ListRow>
      );
    });
    return (
      <>
        <ListHeader columns={GROUP_COLUMNS} />
        {this.emptyOr(
          rows,
          <EmptyState
            compact
            icon={Users}
            title="No protection groups yet"
            message="A group protects VMs of one site to another with one policy. SwiftIQ checks what the two sites can do together when it is created."
            secondaryLabel="New group"
            onSecondary={() => this.setState({ dialog: { kind: "group" } })}
          />,
        )}
      </>
    );
  }

  renderPlans() {
    const text = this.props.query.get("q");
    const groupName = (id: string) => this.group(id)?.name || id;
    const rows = this.state.plans
      .filter(pl => pl.state !== "ARCHIVED" || this.props.query.get("archived"))
      .filter(pl => !text || matchesSearch(text, [pl.name, pl.description, ...pl.group_ids.map(groupName)]))
      .map(pl => {
        const st = PLAN_STATES[pl.state] || { label: pl.state, tone: "neutral" as const };
        const v = pl.last_validation;
        return (
          <ListRow key={pl.id} columns={PLAN_COLUMNS} onClick={() => this.goTab("plans", pl.id)} testId="dr-plan-row">
            <Stack>
              <Primary>{pl.name}</Primary>
              {pl.description ? <Secondary>{pl.description}</Secondary> : null}
            </Stack>
            <Cell title={pl.group_ids.map(groupName).join(", ")}>{pl.group_ids.map(groupName).join(", ")}</Cell>
            <Cell>{pl.current_version}</Cell>
            <div>
              <Pill tone={st.tone}>{st.label}</Pill>
            </div>
            <div>
              {v ? (
                <Pill tone={CHECK_TONES[v.overall]} title={formatShortDateTime(v.at)}>
                  {v.overall === "FAIL" ? `${v.counts.FAIL} failed` : v.overall === "WARNING" ? `${v.counts.WARNING} warning(s)` : "Passed"}
                  {v.version !== pl.current_version ? ` (v${v.version})` : ""}
                </Pill>
              ) : (
                <Muted>Never</Muted>
              )}
            </div>
          </ListRow>
        );
      });
    return (
      <>
        <ListHeader columns={PLAN_COLUMNS} />
        {this.emptyOr(
          rows,
          <EmptyState
            compact
            icon={ListChecks}
            title="No DR plans yet"
            message="A plan says which protected VMs a recovery brings up, in which order, and how they are reached at the DR site. SwiftIQ validates it before a recovery uses it."
            secondaryLabel={this.state.groups.length ? "New plan" : undefined}
            onSecondary={this.state.groups.length ? () => this.setState({ dialog: { kind: "plan" } }) : undefined}
          />,
        )}
      </>
    );
  }

  renderPolicies() {
    const text = this.props.query.get("q");
    const rows = this.state.policies
      .filter(p => !text || matchesSearch(text, [p.name, p.description]))
      .map(p => (
        <ListRow
          key={p.id}
          columns={POLICY_COLUMNS}
          onClick={() => this.setState({ dialog: { kind: "policy", policy: p } })}
          actions={[
            { label: "Edit…", action: () => this.setState({ dialog: { kind: "policy", policy: p } }) },
            { label: "Delete…", hidden: p.protection_group_ids.length > 0, action: () => this.deleteRecord("policy", p.id, p.name, "policies") },
          ]}
          testId="dr-policy-row"
        >
          <Stack>
            <Primary>{p.name}</Primary>
            {p.description ? <Secondary>{p.description}</Secondary> : null}
          </Stack>
          <Cell>{formatSeconds(p.rpo_seconds)}</Cell>
          <Cell>{formatSeconds(p.sync_interval_seconds)}</Cell>
          <Cell>
            {p.consistency === "application" ? "Application" : "Crash"}
            {p.require_application_consistency ? " (required)" : ""}
          </Cell>
          <Cell>{p.retention?.restore_points}</Cell>
          <Cell>{p.protection_group_ids.length}</Cell>
        </ListRow>
      ));
    return (
      <>
        <ListHeader columns={POLICY_COLUMNS} />
        {this.emptyOr(
          rows,
          <EmptyState
            compact
            icon={Timer}
            title="No protection policies yet"
            message="A policy sets the RPO, how often VMs sync, their consistency and how many restore points are kept."
            secondaryLabel="New policy"
            onSecondary={() => this.setState({ dialog: { kind: "policy" } })}
          />,
        )}
      </>
    );
  }

  renderSites() {
    const text = this.props.query.get("q");
    const rows = this.state.sites
      .filter(s => !text || matchesSearch(text, [s.name, s.description, ...s.endpoints.map(e => e.name)]))
      .map(s => {
        const used = this.state.groups.filter(g => g.source_site_id === s.id || g.target_site_id === s.id);
        return (
          <ListRow
            key={s.id}
            columns={SITE_COLUMNS}
            onClick={() => this.setState({ dialog: { kind: "site", site: s } })}
            actions={[
              { label: "Edit…", action: () => this.setState({ dialog: { kind: "site", site: s } }) },
              { label: "Delete…", hidden: used.length > 0, action: () => this.deleteRecord("site", s.id, s.name, "sites") },
            ]}
            testId="dr-site-row"
          >
            <Stack>
              <Primary>{s.name}</Primary>
              {s.description ? <Secondary>{s.description}</Secondary> : null}
            </Stack>
            <Cell>{s.kind === "cloud" ? "Cloud" : "On-premises"}</Cell>
            <Cell title={s.endpoints.map(e => e.name).join(", ")}>
              {s.endpoints.length ? s.endpoints.map(e => `${e.name} (${platformName(e.type)})`).join(", ") : <Muted>None</Muted>}
            </Cell>
            <Cell>{used.length ? pluralise(used.length, "group") : <Muted>—</Muted>}</Cell>
            <Cell>{formatShortDateTime(s.created_at)}</Cell>
          </ListRow>
        );
      });
    return (
      <>
        <ListHeader columns={SITE_COLUMNS} />
        {this.emptyOr(
          rows,
          <EmptyState
            compact
            icon={MapPin}
            title="No sites yet"
            message="Create a site for where VMs run (a Nutanix data centre) and one for where they recover (an AWS or Azure account), each with its SwiftIQ endpoint."
            secondaryLabel="New site"
            onSecondary={() => this.setState({ dialog: { kind: "site" } })}
          />,
        )}
      </>
    );
  }

  // ---- dialogs and drawers ---------------------------------------------------------------------

  renderDialog() {
    const d = this.state.dialog;
    const s = this.state;
    if (!d) return null;
    if (d.kind === "site") {
      return (
        <SiteDialog
          site={d.site}
          sites={s.sites}
          endpoints={s.endpoints}
          onClose={this.closeDialog}
          onSaved={site => this.afterChange(`The site ${site.name} was saved.`)}
        />
      );
    }
    if (d.kind === "plan") {
      return (
        <PlanDialog
          plan={d.plan}
          groups={s.groups}
          workloads={s.workloads}
          onClose={this.closeDialog}
          onSaved={pl => {
            this.afterChange(`The plan ${pl.name} was saved (version ${pl.current_version}).`);
            this.goTab("plans", pl.id);
          }}
        />
      );
    }
    if (d.kind === "policy") {
      return (
        <PolicyDialog policy={d.policy} onClose={this.closeDialog} onSaved={p => this.afterChange(`The policy ${p.name} was saved.`)} />
      );
    }
    if (d.kind === "group") {
      return (
        <GroupDialog
          group={d.group}
          sites={s.sites}
          policies={s.policies}
          onClose={this.closeDialog}
          onSaved={g => {
            this.afterChange(`The protection group ${g.name} was saved.`);
            this.goTab("groups", g.id);
          }}
        />
      );
    }
    if (d.kind === "add") {
      return (
        <AddWorkloadsDialog
          groups={s.groups}
          sites={s.sites}
          workloads={s.workloads}
          initialGroupId={d.groupId}
          onClose={this.closeDialog}
          onAdded={added => {
            this.afterChange(`${pluralise(added.length, "VM")} added. Protect each one to start replicating it.`);
            this.goTab("vms");
          }}
        />
      );
    }
    if (d.kind === "protect") {
      const group = this.group(d.workload.group_id);
      return (
        <ProtectDialog
          workload={d.workload}
          group={group}
          targetEndpoint={this.targetEndpoint(group)}
          transfers={s.transfers}
          onClose={this.closeDialog}
          onOpenWizard={() => this.props.onNavigate("/wizard/replica")}
          onProtected={w => {
            if (w.warnings?.length) this.setState(prev => ({ warnings: { ...prev.warnings, [w.id]: w.warnings! } }));
            this.afterChange(`${w.name} is protected.`);
            this.goTab("vms", w.id);
          }}
        />
      );
    }
    return (
      <ConfirmDialog
        title={d.title}
        message={d.message}
        confirmLabel={d.confirmLabel}
        busyLabel={d.busyLabel}
        danger={d.danger}
        reason={d.reason}
        onClose={this.closeDialog}
        onConfirm={d.run}
      />
    );
  }

  renderDrawer() {
    const { tab, selectedId } = this.props;
    if (!selectedId) return null;
    if (tab === "vms") {
      const w = this.state.workloads.find(x => x.id === selectedId);
      if (!w) return null;
      const group = this.group(w.group_id);
      return (
        <WorkloadDrawer
          workload={{ ...w, warnings: this.state.warnings[w.id] || w.warnings }}
          group={group}
          policy={this.policy(group?.policy_id)}
          sourceEndpoint={this.endpointRef(w.source_endpoint_id)}
          targetEndpoint={this.targetEndpoint(group)}
          transferName={this.transferName(w.replication?.transfer_id)}
          onClose={() => {
            this.setState(prev => ({ warnings: { ...prev.warnings, [w.id]: [] } }));
            this.goTab("vms");
          }}
          onAction={action => this.workloadAction(w, action)}
          onNavigate={this.props.onNavigate}
        />
      );
    }
    if (tab === "plans") {
      const pl = this.state.plans.find(x => x.id === selectedId);
      if (!pl) return null;
      return (
        <PlanDrawer
          plan={pl}
          groups={this.state.groups}
          workloads={this.state.workloads}
          onClose={() => this.goTab("plans")}
          onEdit={full => this.setState({ dialog: { kind: "plan", plan: full } })}
          onChanged={() => this.load(true)}
          onArchive={() =>
            this.confirm({
              title: `Archive the plan ${pl.name}?`,
              message: "An archived plan keeps its versions and runs but cannot change or run again.",
              confirmLabel: "Archive",
              busyLabel: "Archiving…",
              danger: true,
              run: async () => {
                await drSource.archivePlan(pl.id);
                await this.afterChange(`The plan ${pl.name} was archived.`);
                this.goTab("plans");
              },
            })
          }
        />
      );
    }
    if (tab === "groups") {
      const g = this.group(selectedId);
      if (!g) return null;
      return (
        <GroupDrawer
          group={g}
          policy={this.policy(g.policy_id)}
          sourceSite={this.site(g.source_site_id)}
          targetSite={this.site(g.target_site_id)}
          workloads={this.state.workloads.filter(w => w.group_id === g.id)}
          onClose={() => this.goTab("groups")}
          onEdit={() => this.setState({ dialog: { kind: "group", group: g } })}
          onAddVms={() => this.setState({ dialog: { kind: "add", groupId: g.id } })}
          onDelete={() => this.deleteRecord("protection_group", g.id, g.name, "groups")}
        />
      );
    }
    return null;
  }

  renderBody() {
    const tab = this.props.tab;
    if (tab === "groups") return this.renderGroups();
    if (tab === "plans") return this.renderPlans();
    if (tab === "policies") return this.renderPolicies();
    if (tab === "sites") return this.renderSites();
    if (tab === "settings") return <DrClientSettingsView />;
    if (tab === "alerts") return <DrAlertsView onCount={this.setAlertCount} />;
    if (tab === "clients") {
      return (
        <DrOverviewView
          onOpened={() => {
            this.goTab("vms");
            this.load();
          }}
        />
      );
    }
    return this.renderVms();
  }

  render() {
    const s = this.state;
    const attention = s.workloads.filter(needsAttention).length;
    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="dr" />}
          listNoMargin
          headerComponent={
            <PageHeader
              title="Disaster recovery"
              subtitle="Protect VMs to a DR site: SwiftIQ keeps them synced to their policy and shows how much data a recovery would lose."
              pill={attention ? `${attention} need${attention === 1 ? "s" : ""} attention` : undefined}
              pillTone={attention ? "danger" : undefined}
              onProjectChange={() => this.load()}
            />
          }
          listComponent={
            s.disabled ? (
              <Card>
                <EmptyState
                  icon={ShieldCheck}
                  tone="neutral"
                  // Was: title "Disaster recovery is not enabled on this SwiftIQ", message "An administrator turns it
                  // on with SWIFT_DR_ENABLED=true in the SwiftIQ configuration." (no setting names in the UI)
                  title="Disaster recovery isn't turned on"
                  message="It isn't turned on for this organization. Contact Tech9IQ support to enable it."
                />
              </Card>
            ) : (
              <Card>
                {s.error ? (
                  <div style={{ padding: "12px 16px 0 16px" }}>
                    <Notice tone="danger" title="SwiftIQ did not answer">
                      {s.error}
                    </Notice>
                  </div>
                ) : null}
                {userStore.loggedUser?.isAdmin === false ? (
                  <div style={{ padding: "12px 16px 0 16px" }}>
                    <ClientAccessBanner key={userStore.loggedUser?.project?.id} onLeft={() => this.load()} />
                  </div>
                ) : null}
                <TabsBar>
                  <DetailsTabs
                    items={[
                      { label: "Protected VMs", value: "vms", count: s.workloads.length },
                      { label: "Protection groups", value: "groups", count: s.groups.length },
                      { label: "Plans", value: "plans", count: s.plans.filter(pl => pl.state !== "ARCHIVED").length },
                      { label: "Policies", value: "policies", count: s.policies.length },
                      { label: "Sites", value: "sites", count: s.sites.length },
                      { label: "Alerts", value: "alerts", count: s.alertCount ?? undefined },
                      { label: "Client settings", value: "settings" },
                      ...(userStore.loggedUser?.isAdmin ? [{ label: "All clients", value: "clients" }] : []),
                    ]}
                    selectedValue={this.props.tab}
                    onChange={value => this.goTab(value as DrTab)}
                  />
                </TabsBar>
                {s.loaded ? this.renderSetup() : null}
                {["settings", "clients", "alerts"].includes(this.props.tab) ? null : this.renderToolbar()}
                <Scroll>
                  <Inner minWidth={this.props.tab === "vms" ? 1000 : 860}>{this.renderBody()}</Inner>
                </Scroll>
                <Footnote>
                  Replication runs as scheduled syncs (Level 1), not continuous data protection: a recovery returns the
                  last recovery point. Failovers, test failovers and failbacks run from a plan.
                </Footnote>
              </Card>
            )
          }
        />
        {this.renderDialog()}
        {this.renderDrawer()}
      </Wrapper>
    );
  }
}

function DrPage() {
  const query = useListQuery();
  const navigate = useNavigate();
  const params = useParams();
  const tab = (TABS as readonly string[]).includes(params.tab || "") ? (params.tab as DrTab) : "vms";
  return <DrPageContent tab={tab} selectedId={params.id || null} query={query} onNavigate={navigate} />;
}

export default DrPage;
